import { useRef, useEffect, useState } from "react";

import { motion, AnimatePresence } from "framer-motion";

import Badge3D from "./Badge3D";
import {
  getBadgeProgress,
  BADGE_CATEGORIES,
  getBadgeProgression,
  checkBadgeRequirement,
} from "./badgeDefinitions";
import OctagonBadge from "./OctagonBadge";

const WavyBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const parent = canvas.parentElement;

    canvas.width = parent.offsetWidth * 2;
    canvas.height = parent.offsetHeight * 2;

    const ctx = canvas.getContext("2d");
    const { width } = canvas;
    const { height } = canvas;

    ctx.clearRect(0, 0, width, height);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.1)";
    ctx.lineWidth = 1.5;

    const lineSpacing = 16;
    const amplitude = 40;
    const frequency = 0.02;

    for (let x = -150; x < width + 150; x += lineSpacing) {
      ctx.beginPath();

      for (let y = 0; y <= height; y += 2) {
        const waveOffset =
          Math.sin(y * frequency) * amplitude +
          Math.sin(y * frequency * 0.5 + x * 0.008) * amplitude * 0.5;

        const xPos = x + waveOffset;

        if (y === 0) {
          ctx.moveTo(xPos, y);
        } else {
          ctx.lineTo(xPos, y);
        }
      }

      ctx.stroke();
    }
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
};

const BadgeDetailModal = ({
  badge: initialBadge,
  isOpen,
  onClose,
  isEarned: initialIsEarned,
  resellerStats,
}) => {
  // Track the currently displayed badge and history for back navigation
  const [currentBadge, setCurrentBadge] = useState(initialBadge);
  const [badgeHistory, setBadgeHistory] = useState([]);
  const [isCurrentEarned, setIsCurrentEarned] = useState(initialIsEarned);

  // Reset state when modal opens with a new badge
  useEffect(() => {
    if (isOpen && initialBadge) {
      setCurrentBadge(initialBadge);
      setIsCurrentEarned(initialIsEarned);
      setBadgeHistory([]);
    }
  }, [isOpen, initialBadge, initialIsEarned]);

  // Handle clicking on a previous achievement badge
  const handlePreviousBadgeClick = (prevBadge) => {
    // Save current badge to history for back navigation
    setBadgeHistory((prev) => [
      ...prev,
      { badge: currentBadge, isEarned: isCurrentEarned },
    ]);
    setCurrentBadge(prevBadge);
    setIsCurrentEarned(checkBadgeRequirement(prevBadge, resellerStats));
  };

  // Handle going back to previous badge
  const handleGoBack = () => {
    if (badgeHistory.length > 0) {
      const lastItem = badgeHistory[badgeHistory.length - 1];

      setCurrentBadge(lastItem.badge);
      setIsCurrentEarned(lastItem.isEarned);
      setBadgeHistory((prev) => prev.slice(0, -1));
    }
  };

  // Handle going back to the original badge immediately
  const handleGoToOriginal = () => {
    setCurrentBadge(initialBadge);
    setIsCurrentEarned(initialIsEarned);
    setBadgeHistory([]);
  };

  // Check if we're viewing a different badge than the original
  const isViewingDifferentBadge =
    badgeHistory.length > 0 || currentBadge?.id !== initialBadge?.id;

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        if (badgeHistory.length > 0) {
          handleGoBack();
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, badgeHistory]);

  if (!currentBadge) return null;

  // Use currentBadge instead of badge for rendering
  const badge = currentBadge;
  const isEarned = isCurrentEarned;

  const progress = isEarned ? 100 : getBadgeProgress(badge, resellerStats);

  // Get category display name
  const getCategoryName = (category) => {
    switch (category) {
      case BADGE_CATEGORIES.USERS:
        return "User Milestones";
      case BADGE_CATEGORIES.DOMAINS:
        return "Domain Expansion";
      case BADGE_CATEGORIES.LOYALTY:
        return "Loyalty & Tenure";
      case BADGE_CATEGORIES.PERFORMANCE:
        return "Performance & Tiers";
      case BADGE_CATEGORIES.SPECIAL:
        return "Special Achievements";
      default:
        return "Achievement";
    }
  };

  // Get requirement display text
  const getRequirementText = (badge) => {
    const { type, value } = badge.requirement;

    switch (type) {
      case "min_users":
        return `Reach ${value} active users`;
      case "min_domains":
        return `Add ${value} service domains`;
      case "min_days":
        return `Be a reseller for ${value} days`;
      case "min_tier_order":
        return `Reach tier level ${value}`;
      case "joined_before":
        return `Join before ${new Date(value).toLocaleDateString()}`;
      case "weekly_growth":
        return `Add ${value}+ users in one week`;
      case "no_churn_days":
        return `Maintain perfect retention for ${value} days`;
      case "slow_growth":
        return `Grow steadily with ${value}+ users over time`;
      default:
        return "Complete the requirement";
    }
  };

  // Get current progress text
  const getCurrentProgressText = (badge, stats) => {
    const { type, value } = badge.requirement;

    switch (type) {
      case "min_users":
        return `${stats.totalUsers || 0} / ${value} users`;
      case "min_domains":
        return `${stats.uniqueDomains || 0} / ${value} domains`;
      case "min_days": {
        if (!stats.joinedDate) return `0 / ${value} days`;
        const joinDate = new Date(stats.joinedDate);
        const now = new Date();
        const daysDiff = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24));

        return `${daysDiff} / ${value} days`;
      }
      case "min_tier_order":
        return `Tier ${stats.tierOrder || 0} / ${value}`;
      case "weekly_growth":
        return `${stats.weeklyGrowth || 0} / ${value} users this week`;
      case "slow_growth":
        return `${stats.totalUsers || 0} / ${value} users`;
      default:
        return "";
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          {/* Backdrop */}
          <motion.div
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-700/50"
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Header with gradient based on badge rarity */}
            <div
              className="relative py-4 flex flex-col items-center justify-center overflow-hidden"
              style={{
                background: isEarned
                  ? `linear-gradient(135deg, ${badge.gradient[0]}40 0%, ${badge.gradient[1]}60 50%, ${badge.gradient[2]}40 100%)`
                  : "linear-gradient(135deg, #374151 0%, #1F2937 100%)",
              }}
            >
              {/* Wavy lines background */}
              <WavyBackground />

              {/* Decorative circles */}
              <div className="absolute top-4 left-4 w-20 h-20 rounded-full bg-white/5 blur-xl" />
              <div className="absolute bottom-4 right-4 w-16 h-16 rounded-full bg-white/5 blur-xl" />

              {/* 3D Badge */}
              <div className="relative z-10 w-full">
                <Badge3D badge={badge} isEarned={isEarned} />
              </div>

              {/* Badge name and rarity - below the badge, in one line */}
              <div
                className="relative z-10 mb-2 flex items-center justify-center gap-2 bg-black/20 px-4 py-2 rounded-lg shadow-lg"
                style={{ textShadow: "0 2px 4px rgba(0,0,0,0.5)" }}
              >
                <h2 className="text-lg font-bold text-white">{badge.name}</h2>
                {badge.rarity && (
                  <>
                    <span className="text-white/50">/</span>
                    <span
                      className="text-sm font-medium"
                      style={{
                        color:
                          badge.rarity.color === "gray"
                            ? "#9CA3AF"
                            : badge.rarity.color === "green"
                              ? "#22C55E"
                              : badge.rarity.color === "blue"
                                ? "#3B82F6"
                                : badge.rarity.color === "purple"
                                  ? "#A855F7"
                                  : badge.rarity.color === "orange"
                                    ? "#F97316"
                                    : badge.rarity.color === "pink"
                                      ? "#EC4899"
                                      : "#9CA3AF",
                      }}
                    >
                      {badge.rarity.name}
                    </span>
                  </>
                )}
              </div>

              {/* Back button - shown when viewing a previous badge */}
              {badgeHistory.length > 0 && (
                <button
                  className="absolute top-3 left-3 z-20 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white transition-all bg-black/30 hover:bg-black/50 rounded-full backdrop-blur-sm"
                  title="Go back"
                  onClick={handleGoBack}
                >
                  <i className="fas fa-arrow-left text-lg" />
                </button>
              )}

              {/* Current Achievement - show original badge on the right, blurred */}
              {isViewingDifferentBadge && initialBadge && (
                <div
                  className="absolute top-3 right-16 z-20 cursor-pointer group"
                  title={`Return to ${initialBadge.name}`}
                  onClick={handleGoToOriginal}
                >
                  <div className="transform scale-[0.7] transition-all duration-300 group-hover:scale-[0.85] blur-[2px] group-hover:blur-0 opacity-50 group-hover:opacity-100">
                    <OctagonBadge
                      badge={initialBadge}
                      index={0}
                      isEarned={initialIsEarned}
                      showTooltip={false}
                      size="xs"
                    />
                  </div>
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <span className="text-[8px] text-white bg-black/80 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                      Current
                    </span>
                  </div>
                </div>
              )}

              {/* Close button */}
              <button
                className="absolute top-3 right-3 z-20 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white transition-all bg-black/30 hover:bg-black/50 rounded-full backdrop-blur-sm"
                onClick={onClose}
              >
                <i className="fas fa-times text-lg" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Category */}
              <div className="text-center mb-4">
                <span className="text-xs text-gray-400 uppercase tracking-wider">
                  {getCategoryName(badge.category)}
                </span>
              </div>

              {/* Description */}
              <p className="text-gray-300 text-center mb-6">
                {badge.longDescription || badge.description}
              </p>

              {/* Progress (only shown for locked badges) */}
              {!isEarned && (
                <div className="bg-gray-700/30 border border-gray-600/50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Progress</span>
                    <span className="text-white font-semibold">
                      {Math.round(progress)}%
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden mb-3">
                    <motion.div
                      animate={{ width: `${progress}%` }}
                      className="h-full rounded-full"
                      initial={{ width: 0 }}
                      style={{
                        background: `linear-gradient(90deg, ${badge.gradient[0]}, ${badge.gradient[1]})`,
                      }}
                      transition={{ duration: 1, ease: "easeOut" }}
                    />
                  </div>
                  {/* Current progress text */}
                  <div className="text-center text-gray-400 text-sm">
                    {getCurrentProgressText(badge, resellerStats)}
                  </div>
                </div>
              )}

              {/* Badge Progression History - show previous badges in the same progression */}
              {badge.progression &&
                (() => {
                  const progression = getBadgeProgression(badge);
                  // Get badges earned BEFORE this one in the progression
                  const previousBadges = progression.filter(
                    (b) =>
                      b.progressionOrder < badge.progressionOrder &&
                      checkBadgeRequirement(b, resellerStats)
                  );

                  if (previousBadges.length === 0) return null;

                  return (
                    <div className="mb-4">
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">
                        Previous Achievements
                      </div>
                      <div className="flex gap-3 justify-start overflow-x-auto overflow-y-visible pt-2 pb-2 px-1 scrollbar-hide">
                        {previousBadges
                          .sort(
                            (a, b) => b.progressionOrder - a.progressionOrder
                          )
                          .map((prevBadge, index) => (
                            <div
                              className="flex flex-col items-center flex-shrink-0 hover:z-50 relative cursor-pointer group"
                              key={prevBadge.id}
                              onClick={() =>
                                handlePreviousBadgeClick(prevBadge)
                              }
                            >
                              <div className="transform transition-transform group-hover:scale-110">
                                <OctagonBadge
                                  badge={prevBadge}
                                  index={index}
                                  isEarned={true}
                                  showTooltip={false}
                                  size="xs"
                                />
                              </div>
                              <span className="text-[10px] text-gray-400 mt-1 text-center whitespace-nowrap group-hover:text-white transition-colors">
                                {prevBadge.name}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  );
                })()}

              {/* Requirement */}
              <div className="bg-gray-800/50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-700/50 flex items-center justify-center">
                    <i className="fas fa-bullseye text-gray-400" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">
                      Requirement
                    </div>
                    <div className="text-white text-sm">
                      {getRequirementText(badge)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-6">
              {badgeHistory.length > 0 ? (
                <div className="flex gap-3">
                  <button
                    className="flex-1 py-3 px-6 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold hover:from-purple-500 hover:to-pink-500 transition-all duration-200 flex items-center justify-center gap-2"
                    onClick={handleGoBack}
                  >
                    <i className="fas fa-arrow-left" />
                    Back
                  </button>
                  <button
                    className="flex-1 py-3 px-6 rounded-xl bg-gradient-to-r from-gray-700 to-gray-600 text-white font-semibold hover:from-gray-600 hover:to-gray-500 transition-all duration-200"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <button
                  className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-gray-700 to-gray-600 text-white font-semibold hover:from-gray-600 hover:to-gray-500 transition-all duration-200"
                  onClick={onClose}
                >
                  Close
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BadgeDetailModal;
